import _ from 'lodash';
import { useEffect } from 'react';
import { ImageTypeEnum } from 'api';
import { useRootStore } from 'stores';
import { CreativeImage } from '../types';
import { useQuery, articleGroupUrl } from 'utils';
import { getCallToActionTextAssets } from '../utils/callToActionTextAssets';
import { CampaignObjectivesEnum, CreativeTemplatesEntities, Views } from '../constants';

const { IMAGES, VIDEOS, Headlines, PostTexts, Descriptions, CTAs } = CreativeTemplatesEntities;

export const useCreativeTemplatesBuilder = () => {
  const qs = useQuery();

  const rootStore = useRootStore();
  const store = rootStore.creativeTemplatesBuilderStore;

  const viewIndex = Object.values(Views).indexOf(store.view);

  const articleGroupLink = store.articleGroup?.id ? articleGroupUrl(store.articleGroup.id, 'creative_templates') : null;
  const openCreateCreativesResultDialog = store.openCreateCreativesResultDialog;

  const handleViewChange = (newViewIndex: number): void => {
    const currentView = Object.keys(Views)[newViewIndex] as keyof typeof Views;
    store.setView(Views[currentView]);
  };

  const preSelectInputs = {
    [VIDEOS]: qs.getAll('videos'),
    [IMAGES]: qs.getAll('images'),
    [Headlines]: qs.getAll('headline_ids'),
    [PostTexts]: qs.getAll('post_text_ids'),
    [Descriptions]: qs.getAll('description_ids'),
    [CTAs]: qs.getAll('call_to_actions')
  };

  const setPreselectedAssets = () => {
    const isAnyAssetSelected = _.some(store.imageOrVideoCreativeTemplateInputs, (input) => input.length > 0);
    if (isAnyAssetSelected) return;

    Object.entries(preSelectInputs).forEach(([key, value]) => {
      if (!value || !store.articleGroup) return;

      const { headlines, descriptions, postTexts } = store.articleGroup;

      if (key === IMAGES) {
        const images = value
          .map((image) => {
            const [id, type] = image.split('-');
            return store.images.find((_image) => _image.id.toString() === id && _image.type === type);
          })
          .filter(Boolean);

        store.preSelectImages(images as CreativeImage[]);
      }

      if (key === VIDEOS) {
        store.preSelectVideos(value);
      }

      if (key === Headlines) {
        const textAssets = headlines.filter(({ id }) => _.includes(value, id));
        store.preSelectTextAssets(textAssets, Headlines);
      }

      if (key === PostTexts) {
        const textAssets = postTexts.filter(({ id }) => _.includes(value, id));
        store.preSelectTextAssets(textAssets, PostTexts);
      }

      if (key === Descriptions) {
        const textAssets = descriptions.filter(({ id }) => _.includes(value, id));
        store.preSelectTextAssets(textAssets, Descriptions);
      }

      if (key === CTAs) {
        const ctas = getCallToActionTextAssets(CampaignObjectivesEnum.ALL_CAMPAIGN_OBJECTIVES);
        const textAssets = ctas.filter(({ id }) => _.includes(value, id));
        store.preSelectTextAssets(textAssets, CTAs);
      }
    });
  };

  useEffect(() => {
    return () => {
      rootStore.resetCreativeTemplatesBuilderStore();
    };
  }, []);

  useEffect(() => {
    if (store.isTextAssetsPreselected || !store.articleGroup) return;

    setPreselectedAssets();

    store.setIsTextAssetsPreselected(true);
  }, [store.articleGroup]);

  useEffect(() => {
    const anyCreativeImage = store.images.find((image) => image.type === ImageTypeEnum.CreativeImage);
    const anyArticleImage = store.images.find((image) => image.type === ImageTypeEnum.ArticleImage);

    if (
      (anyCreativeImage && !store.isCreativeImagesPreselected) ||
      (anyArticleImage && !store.isArticleImagesPreselected)
    ) {
      setPreselectedAssets();
    }

    anyCreativeImage && !store.isCreativeImagesPreselected && store.setIsCreativeImagesPreselected(true);
    anyArticleImage && !store.isArticleImagesPreselected && store.setIsArticleImagesPreselected(true);
  }, [store.images]);

  useEffect(() => {
    if (store.videos && !store.isVideoPreselected) {
      setPreselectedAssets();
    }

    store.videos && !store.isVideoPreselected && store.setIsVideoPreselected(true);
  }, [store.videos]);

  return { openCreateCreativesResultDialog, viewIndex, articleGroupLink, handleViewChange };
};
